import React, { useEffect, useState } from 'react';
import { Button, Grid, Modal } from '@material-ui/core';
import { AppTextfield } from '../parts/input/AppTextfield';
import { AppSelectfield } from '../parts/input/AppSelectfield';
import { PreviewField } from './Preview';
import { AppDatePicker } from '../parts/input/AppDatePicker';
import { formatDatetime } from '../../utils/common';
import { TSelectItem } from '../../types/common';
import { SelectIcon } from '../parts/cloudStorage/SelectIcon';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

type Tprops = {
  file: File;
  index: number;
  setValue: any;
  vendorsList: TSelectItem[];
  fileTypes: TSelectItem[];
  deleteFile: (index: number) => void;
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: '24px 0',
    borderBottom: 'solid 1px #E6E6E6',
    position: 'relative'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  column: {
    display: 'flex',
    flexDirection: 'column'
  },
  fullWidth: {
    width: '100%'
  },
  item: {
    minWidth: '70px',
    marginRight: '20px'
  },
  previewButton: {
    backgroundColor: '#52555F',
    color: '#fff'
  },
  deleteIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
    cursor: 'pointer'
  },
  descriptionField: {
    width: 'calc(100% - 20px)'
  }
}));

const AppSelectfieldWrapper: React.FC<{
  className: string;
  name: string;
  listDatas: TSelectItem[];
  defaultValue: { value: number; label: string };
}> = ({ className, ...props }) => (
  <div className={className}>
    <AppSelectfield {...props} />
  </div>
);

const AppTextfieldWrapper: React.FC<{
  className: string;
  name: string;
  multiline?: boolean;
  rows?: number;
}> = ({ className, ...props }) => (
  <div className={className}>
    <AppTextfield {...props} />
  </div>
);

const AppDatePickerWrapper: React.FC<{
  className: string;
  name: string;
  defaultValue: string;
}> = ({ className, ...props }) => (
  <div className={className}>
    <AppDatePicker {...props} />
  </div>
);

export const List: React.FC<Tprops> = (props: Tprops) => {
  const { file, index, setValue, vendorsList, deleteFile, fileTypes } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [src, setSrc] = useState<string>('');
  const [openPreview, setOpenPreview] = useState<boolean>(false);

  const previewPdf = (file: File) => {
    const reader = new FileReader();
    setSrc(URL.createObjectURL(file));
  };

  useEffect(() => {
    setValue(`list.${index}.upload_file`, file);
    setValue(`list.${index}.name`, file.name);
    setValue(`list.${index}.file_type_id`, { value: 0, label: '--' });
    setValue(`list.${index}.send_to`, { value: 0, label: '' });
    setValue(`list.${index}.receive_from`, { value: 0, label: '--' });
  }, [file]);

  return (
    <>
      <div className={classes.root}>
        <CloseIcon
          className={classes.deleteIcon}
          fontSize='large'
          onClick={() => deleteFile(index)}
        />
        <Grid
          container
          justify='center'
          spacing={1}
          className={isMobile ? classes.column : classes.row}
        >
          <Grid item xs={12}>
            <Grid
              container
              spacing={1}
              className={isMobile ? classes.column : classes.row}
            >
              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>書類</div>
                <div style={{ marginRight: 12 }}>
                  <SelectIcon
                    width={31}
                    height={38}
                    format={file.type}
                    name={file.name}
                  />
                </div>
                <div
                  className={classes.fullWidth}
                  style={{
                    wordBreak: 'break-all',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {file.name}
                </div>
              </Grid>

              {file.type === 'application/pdf' && (
                <Grid
                  item
                  xs={12}
                  className={isMobile ? classes.column : classes.row}
                >
                  <Button
                    className={classes.previewButton}
                    variant='contained'
                    onClick={() => {
                      previewPdf(file);
                      setOpenPreview(true);
                    }}
                  >
                    <VisibilityIcon />
                    プレビュー
                  </Button>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>書類種別</div>
                <AppSelectfieldWrapper
                  name={`list.${index}.file_type_id`}
                  listDatas={fileTypes}
                  defaultValue={{ value: 0, label: '--' }}
                  className={classes.fullWidth}
                />
              </Grid>

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>送付先</div>
                <AppSelectfieldWrapper
                  name={`list.${index}.send_to`}
                  listDatas={vendorsList}
                  defaultValue={{ value: 0, label: '--' }}
                  className={classes.fullWidth}
                />
              </Grid>

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>受取元</div>
                <AppSelectfieldWrapper
                  name={`list.${index}.receive_from`}
                  listDatas={vendorsList}
                  defaultValue={{ value: 0, label: '--' }}
                  className={classes.fullWidth}
                />
              </Grid>

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>金額</div>
                <AppTextfieldWrapper
                  name={`list.${index}.amount`}
                  className={classes.fullWidth}
                />
              </Grid>

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>日付</div>
                <AppDatePickerWrapper
                  name={`list.${index}.exchange_date`}
                  defaultValue={formatDatetime(new Date(), 'YYYY-MM-DD')}
                  className={classes.fullWidth}
                />
              </Grid>

              <Grid
                item
                xs={12}
                className={isMobile ? classes.column : classes.row}
              >
                <div className={classes.item}>説明</div>
                <AppTextfieldWrapper
                  name={`list.${index}.description`}
                  className={classes.descriptionField}
                  multiline={true}
                  rows={3}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
        open={openPreview}
        onClose={() => setOpenPreview(false)}
      >
        <PreviewField
          src={src}
          openPreview={openPreview}
          setOpenPreview={setOpenPreview}
        />
      </Modal>
    </>
  );
};
